import type { FunctionComponent } from 'react';

import type { MerchantReportRiskLevel } from '@ballerine/common';

import { Separator } from '@/components/atoms/Separator';
import { RiskLevelHexagon } from '../../report-shared/components';
import { Tag } from './Tag';

type RiskLevelCardProps = {
  level: string;
  keyFindings?: string[];
};

const formatFinding = (finding: string): string => {
  return finding
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const RiskLevelCard: FunctionComponent<RiskLevelCardProps> = ({ level, keyFindings }) => {
  const tags =
    keyFindings?.map(finding => ({
      text: formatFinding(finding),
      variant: level === 'critical' || level === 'high' ? ('red' as const) : ('yellow' as const),
    })) || [];

  const tagsPerColumn = 4;
  const column1 = tags.slice(0, tagsPerColumn);
  const column2 = tags.slice(tagsPerColumn);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex">
        <div className="flex w-[20%] flex-none flex-col items-center gap-6 text-center">
          <h4 className="text-center text-base font-semibold">RISK LEVEL</h4>
          <div className="scale-140">
            <RiskLevelHexagon riskLevel={level as MerchantReportRiskLevel} />
          </div>
        </div>

        <Separator orientation="vertical" className="self-stretch" />

        <div className="flex w-[80%] flex-none flex-col justify-center pl-6">
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-stone-400">SIGNALS</h4>
          </div>
          {tags.length > 0 ? (
            <div className="flex gap-4">
              <div className="flex flex-1 flex-col gap-2">
                {column1.map((tag, index) => (
                  <div key={`col1-${index}`} className="flex">
                    <Tag text={tag.text} variant={tag.variant} />
                  </div>
                ))}
              </div>
              <div className="flex flex-1 flex-col gap-2">
                {column2.map((tag, index) => (
                  <div key={`col2-${index}`} className="flex">
                    <Tag text={tag.text} variant={tag.variant} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No key findings identified</p>
          )}
        </div>
      </div>
    </div>
  );
};
